import { useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { reaction } from "mobx"
import { observer, useLocalObservable } from "mobx-react"
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Layout,
  Radio,
  RadioChangeEvent,
  Row,
  Select,
  Typography,
} from "antd"
import { SaveOutlined } from "@ant-design/icons"

import { notify } from "@src/app/components/notify"
import { DefaultPageLayout } from "@src/app/layouts/DefaultPageLayout"
import { paths } from "@src/app/paths"
import { BankAccount } from "@src/pages/bankDetailsSlug/entity/BankAccount"
import { SubmitButton } from "@src/shared/componemts/SubmitButton"
import { useCatchFormErrors } from "@src/shared/form/base_error"

export const BankDetailsSlug = observer(() => {
  const { Item } = Form

  const [form] = Form.useForm()

  const { id } = useParams<{
    id: string
  }>()

  const nav = useNavigate()

  const account = useLocalObservable(
    () => new BankAccount(id === "new" ? "" : id),
  )

  const { submit } = useCatchFormErrors(form, async (values) => {
    return account.create(values).then((response) => {
      if (response) {
        notify.success("Bank account has been created")

        nav(paths.settings)
      }
    })
  })

  useEffect(() => {
    reaction(
      () => account.toForm,
      (v) => {
        form.setFieldsValue(v)
      },
    )
  }, [id])

  const handleRadioChange = (e: RadioChangeEvent) => {
    const currency = e.target.value == "SEPA" ? "EUR" : "USD"

    form.setFieldsValue({ currency })
  }

  return (
    <DefaultPageLayout
      button={
        <SubmitButton
          buttonProps={{
            disabled: account.isLoading || account.isReadonly,
            icon: <SaveOutlined />,
            loading: account.isLoading,
            onClick: () => submit(form.getFieldsValue()),
            type: "primary",
          }}
          form={form}
        >
          Save
        </SubmitButton>
      }
      title="Bank Details"
      url={paths.settings}
    >
      <Form
        disabled={account.isLoading || account.isReadonly}
        form={form}
        id="GENERATED_ID_BANKDETAILSSLUG_FORM_VOID_1"
        layout="vertical"
        name="bankDetails"
        onFinish={submit}
        size="large"
        style={{ maxWidth: "100%" }}
      >
        <Row gutter={32}>
          <Col span={12}>
            <Flex gap={16} vertical>
              <Item
                label="Payment method name"
                name="name"
                rules={[
                  { message: "Please input account name", required: true },
                ]}
              >
                <Input placeholder="Enter name" />
              </Item>

              <Flex align="center" gap={16}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  Bank transfer
                </Typography.Title>

                <Item
                  initialValue="SEPA"
                  name="transfer"
                  rules={[
                    {
                      message: "Please select transfer method",
                      required: true,
                    },
                  ]}
                >
                  <Radio.Group
                    buttonStyle="solid"
                    onChange={handleRadioChange}
                    size="small"
                  >
                    <Radio.Button value="SEPA">SEPA</Radio.Button>
                    <Radio.Button value="SWIFT">SWIFT</Radio.Button>
                  </Radio.Group>
                </Item>
              </Flex>

              <Item
                initialValue="EUR"
                label="Currency"
                name="currency"
                rules={[{ message: "Please select currency", required: true }]}
              >
                <Select
                  options={[
                    { label: "USD", value: "USD" },
                    { label: "EUR", value: "EUR" },
                  ]}
                />
              </Item>
            </Flex>
          </Col>
          <Col span={12}>
            <Flex gap={16} vertical>
              <Item
                label="Beneficiary Account name"
                name="beneficiary_account_name"
                rules={[
                  {
                    message: "Please input beneficiary account name",
                    required: true,
                  },
                ]}
              >
                <Input placeholder="Enter name" />
              </Item>

              <Item
                label="Beneficiary IBAN /Account number"
                name="beneficiary_account_number"
                rules={[
                  { message: "Please input account number", required: true },
                ]}
              >
                <Input placeholder="Enter number" />
              </Item>

              <Item
                label="Bank address"
                name="bank_address"
                rules={[
                  { message: "Please input bank address", required: true },
                ]}
              >
                <Input placeholder="Enter address" />
              </Item>

              <Item
                label="Beneficiary address"
                name="beneficiary_address"
                rules={[
                  {
                    message: "Please input beneficiary address",
                    required: true,
                  },
                ]}
              >
                <Input placeholder="Enter address" />
              </Item>

              <Item
                label="Bank name"
                name="bank_name"
                rules={[{ message: "Please input bank name", required: true }]}
              >
                <Input placeholder="Enter name" />
              </Item>

              <Item
                label="BIC/ SWIFT code"
                name="bic_swift_code"
                rules={[
                  {
                    message: "Please input bic or swift code",
                    required: true,
                  },
                ]}
              >
                <Input placeholder="Enter code" />
              </Item>
            </Flex>
          </Col>
        </Row>
      </Form>
    </DefaultPageLayout>
  )
})
